import React, { type CSSProperties, type ReactNode } from "react";
import { DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import { cx, useKitDisabled } from "./fieldContext";
import { useUiText } from "./uiText";
import { KEY_ENTER, KEY_SPACE } from "./keyboardKeys";
const PERCENT_COMPLETE = 100;

/*
 * Dateiauswahl des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6; Ersatz für Antd `Upload`/`Upload.Dragger`). Das Kit lädt
 * nichts selbst hoch: Aufrufer bekommen die Dateien über `beforeUpload` bzw. `onChange` und laden über ihre API —
 * wie heute überall (`beforeUpload` liefert `false`). `Upload.LIST_IGNORE` nimmt eine Datei nicht in die Liste auf.
 */

export type RcFile = File & { uid: string };

export type UploadFile<T = unknown> = {
  uid: string;
  name: string;
  size?: number;
  type?: string;
  status?: "error" | "done" | "uploading" | "removed";
  percent?: number;
  url?: string;
  thumbUrl?: string;
  originFileObj?: RcFile;
  response?: T;
  lastModified?: number;
};

export type UploadChangeParam<T = UploadFile> = { file: T; fileList: T[] };

/** Eigenes Hochladen wie Antd `customRequest`; das Kit setzt danach den Status der Datei. */
export type UploadRequestOption = {
  file: RcFile;
  filename?: string;
  onProgress?: (event: { percent: number }) => void;
  onSuccess?: (response?: unknown) => void;
  onError?: (error: Error) => void;
};

const LIST_IGNORE = "__ck_upload_list_ignore__";
let uidCounter = 0;
const nextUid = () => `ck-upload-${Date.now()}-${uidCounter++}`;

export interface UploadProps {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** `false` oder Versprechen auf `false`: nicht hochladen (Standard im Kit); `Upload.LIST_IGNORE`: nicht aufnehmen. */
  beforeUpload?: (file: RcFile, fileList: RcFile[]) => boolean | string | void | Promise<unknown>;
  fileList?: UploadFile[];
  defaultFileList?: UploadFile[];
  onChange?: (info: UploadChangeParam) => void;
  onRemove?: (file: UploadFile) => boolean | void | Promise<boolean | void>;
  customRequest?: (options: UploadRequestOption) => void | Promise<void>;
  showUploadList?: boolean | { showPreviewIcon?: boolean; showRemoveIcon?: boolean; showDownloadIcon?: boolean };
  maxCount?: number;
  name?: string;
  directory?: boolean;
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
}

function toUploadFile(file: RcFile): UploadFile {
  return { uid: file.uid, name: file.name, size: file.size, type: file.type, lastModified: file.lastModified, originFileObj: file };
}

function useUploadState({ fileList, defaultFileList, onChange, beforeUpload, maxCount, onRemove, customRequest, name }: UploadProps) {
  const [inner, setInner] = React.useState<UploadFile[]>(defaultFileList ?? []);
  const list = fileList ?? inner;
  const listRef = React.useRef(list);
  listRef.current = list;
  const commit = (file: UploadFile, next: UploadFile[]) => {
    if (fileList === undefined) setInner(next);
    onChange?.({ file, fileList: next });
  };
  const add = async (files: File[]) => {
    const rcFiles = files.map((file) => Object.assign(file, { uid: nextUid() }) as RcFile);
    for (const file of rcFiles) {
      let result: unknown = true;
      try { result = beforeUpload ? await beforeUpload(file, rcFiles) : true; } catch { continue; }
      if (result === LIST_IGNORE) continue;
      const upload = customRequest && result !== false;
      const entry: UploadFile = { ...toUploadFile(file), ...(upload ? { status: "uploading" as const, percent: 0 } : {}) };
      let next = [...listRef.current, entry];
      if (maxCount && next.length > maxCount) next = next.slice(next.length - maxCount);
      listRef.current = next;
      commit(entry, next);
      if (upload) {
        const patch = (change: Partial<UploadFile>) => {
          const updated = listRef.current.map((item) => (item.uid === entry.uid ? { ...item, ...change } : item));
          listRef.current = updated;
          commit(updated.find((item) => item.uid === entry.uid) ?? { ...entry, ...change }, updated);
        };
        void customRequest({
          file,
          filename: name,
          onProgress: ({ percent }) => patch({ percent }),
          onSuccess: (response) => patch({ status: "done", percent: PERCENT_COMPLETE, response }),
          onError: () => patch({ status: "error" }),
        });
      }
    }
  };
  const remove = async (file: UploadFile) => {
    const allowed = await onRemove?.(file);
    if (allowed === false) return;
    const next = listRef.current.filter((item) => item.uid !== file.uid);
    listRef.current = next;
    commit({ ...file, status: "removed" }, next);
  };
  return { list, add, remove };
}

function UploadList({ list, config, onRemove, disabled }: { list: UploadFile[]; config: UploadProps["showUploadList"]; onRemove: (file: UploadFile) => void; disabled: boolean }) {
  const { text } = useUiText();
  if (config === false || list.length === 0) return null;
  const showRemove = typeof config === "object" ? config.showRemoveIcon !== false : true;
  return (
    <ul className="ck-upload-list">
      {list.map((file) => (
        <li key={file.uid} className={cx("ck-upload-list-item", file.status === "error" && "ck-upload-list-item-error")}>
          <PaperClipOutlined aria-hidden className="ck-upload-list-item-icon" />
          <span className="ck-upload-list-item-name" title={file.name}>{file.name}</span>
          {showRemove && !disabled ? (
            <button type="button" className="ck-icon-button ck-upload-list-item-remove" aria-label={`${text("Remove")}: ${file.name}`} onClick={() => onRemove(file)}>
              <DeleteOutlined />
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function filesFrom(list: FileList | null, accept: string | undefined): File[] {
  const files = Array.from(list ?? []);
  if (!accept) return files;
  const rules = accept.split(",").map((rule) => rule.trim().toLowerCase()).filter(Boolean);
  return files.filter((file) => rules.some((rule) => rule.startsWith(".")
    ? file.name.toLowerCase().endsWith(rule)
    : rule.endsWith("/*") ? file.type.startsWith(rule.slice(0, -1)) : file.type === rule));
}

function UploadRoot({ drag, ...props }: UploadProps & { drag: boolean }) {
  const { accept, multiple, disabled, showUploadList = true, name, directory, className, rootClassName, style, children, id } = props;
  const mergedDisabled = useKitDisabled(disabled);
  const state = useUploadState(props);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [over, setOver] = React.useState(false);
  const open = () => { if (!mergedDisabled) inputRef.current?.click(); };
  const input = (
    <input
      ref={inputRef}
      type="file"
      className="ck-upload-input"
      id={id}
      name={name}
      accept={accept}
      multiple={multiple}
      disabled={mergedDisabled}
      tabIndex={-1}
      {...(directory ? { webkitdirectory: "" } : {})}
      onChange={(event) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        void state.add(files);
      }}
    />
  );
  const trigger = drag ? (
    <div
      className={cx("ck-upload", "ck-upload-drag", over && "ck-upload-drag-hover", mergedDisabled && "ck-upload-disabled")}
      style={style}
      role="button"
      tabIndex={mergedDisabled ? -1 : 0}
      aria-disabled={mergedDisabled || undefined}
      onClick={open}
      onKeyDown={(event) => { if (event.key === KEY_ENTER || event.key === KEY_SPACE) { event.preventDefault(); open(); } }}
      onDragOver={(event) => { event.preventDefault(); if (!mergedDisabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        if (mergedDisabled) return;
        const files = filesFrom(event.dataTransfer.files, accept);
        void state.add(multiple ? files : files.slice(0, 1));
      }}
    >
      {input}
      <div className="ck-upload-drag-container">{children}</div>
    </div>
  ) : (
    <span className={cx("ck-upload", "ck-upload-select", mergedDisabled && "ck-upload-disabled")} style={style} onClick={open}>
      {input}
      {children}
    </span>
  );
  return (
    <span className={cx("ck-upload-wrapper", drag && "ck-upload-wrapper-drag", rootClassName, className)}>
      {trigger}
      <UploadList list={state.list} config={showUploadList} onRemove={(file) => void state.remove(file)} disabled={mergedDisabled} />
    </span>
  );
}

function UploadBase(props: UploadProps) {
  return <UploadRoot {...props} drag={false} />;
}
function UploadDragger(props: UploadProps) {
  return <UploadRoot {...props} drag />;
}

/** Dateiauswahl per Klick (`Upload`) oder Ziehen und Ablegen (`Upload.Dragger`). */
export const Upload = Object.assign(UploadBase, { Dragger: UploadDragger, LIST_IGNORE });
